import { useEffect, useMemo, useState } from "react";
import { DEFAULT_LIMIT_ITEMS } from "constants/data";
import { MstItemDetailWheel, MstItemDetailWheelSelect } from "../types";
import getWheelListApi, { deleteImageApi, updateWheelApi, uploadImageApi } from "../api/wheelApi";
// import getWheelListApi, { WheelQuery } from "../api/wheelApi";

/**
 * Custom hook to fetch and manage a paginated list of wheels.
 */
const useWheelList = () => {
  const [queryParams, setQueryParams] = useState<Partial<MstItemDetailWheelSelect>>({
    item_cd: "",
    brand_name: "",
    maker_name: "",
  });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [wheels, setWheels] = useState<MstItemDetailWheel[]>([]);
  const totalPage = useMemo(() => Math.ceil(total / DEFAULT_LIMIT_ITEMS), [total]);

  const getWheels = async (body?: MstItemDetailWheelSelect) => {
    try {
      const data = await getWheelListApi(body);
      if (body && body?.offset > 1) {
        setWheels((prev) => [...prev, ...data.list.mst_item_detail_wheel]);
      } else {
        setWheels(data.list.mst_item_detail_wheel);
      }
      setTotal(data.total);
    } catch (error) {
      // do nothing
    }
    setLoading(false);
  };

  useEffect(() => {
    getWheels({ offset: 1 });
  }, []);

  const handleSearch = () => {
    setPage(1);
    getWheels({
      ...queryParams,
      offset: 1,
    });
  };

  const handleUploadImage = async (file: File, item_cd: string) => {
    try {
      await uploadImageApi({ image_file: file, item_cd });
      const data = await getWheelListApi({ item_cd, offset: 1 });
      if (data && data.list.mst_item_detail_wheel.length > 0) {
        const wheel = data.list.mst_item_detail_wheel[0];
        setWheels((prev) => {
          const newWheels = [...prev];
          const index = newWheels.findIndex((w) => w.item_cd === item_cd);
          if (index !== -1) {
            newWheels[index] = {
              ...newWheels[index],
              wheel_image_url: wheel.wheel_image_url,
            };
          }
          return newWheels;
        });
      }
    } catch (error) {
      // do nothing
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteImage = async (item_cd: string) => {
    try {
      await deleteImageApi({ item_cd });
      setWheels((prev) => {
        const newWheels = [...prev];
        const index = newWheels.findIndex((w) => w.item_cd === item_cd);
        if (index !== -1) {
          newWheels[index] = {
            ...newWheels[index],
            wheel_image_url: undefined,
          };
        }
        return newWheels;
      });
    } catch (error) {
      // do nothing
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateWheel = async (wheel: MstItemDetailWheel) => {
    try {
      await updateWheelApi(wheel);
    } catch (error) {
      // do nothing
    } finally {
      setLoading(false);
      getWheels({ ...queryParams, offset: page });
    }
  };

  const onLoadMore = async () => {
    if (page + 1 > totalPage) return;
    setLoading(true);
    setPage((prevPage) => prevPage + 1);
    await getWheels({ ...queryParams, offset: page + 1 });
  };

  return {
    wheels,
    queryParams,
    total,
    totalPage,
    page,
    loading,
    handleSearch,
    onLoadMore,
    setQueryParams,
    handleUploadImage,
    handleDeleteImage,
    handleUpdateWheel,
  };
};

export default useWheelList;
